import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppNavLink } from "../lib/route-primitives";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Heart, Sun, Moon } from "lucide-react";
import { useTheme } from "../hooks/use-theme";
import { UserMenu } from "./user-menu";
import { organizerSupportApi } from "../lib/api";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";

/**
 * Persistent top bar shared across all non-wedding organizer pages
 * (My Weddings, Support, Profile).
 *
 * On mobile, shows "← Düğünlerim" back button when not on the home page.
 * Pass `refreshUnread` to re-trigger unread count fetch (e.g. after reading tickets).
 */
export function OrganizerPageHeader({ refreshUnread }: { refreshUnread?: number } = {}) {
  const { t } = useTranslation();
  const { toggle: toggleTheme, isDark } = useTheme();
  const location = useLocation();
  const themeLabel = isDark ? t("common.switchToLight") : t("common.switchToDark");
  const [hasUnread, setHasUnread] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    let cancelled = false;
    organizerSupportApi.list()
      .then((res) => {
        if (cancelled) return;
        const tickets = (res.data ?? []) as Array<{ organizer_unread: number; status: string }>;
        setHasUnread(
          tickets.some(
            (tk) => tk.organizer_unread > 0 && (tk.status === "open" || tk.status === "in_review"),
          ),
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshUnread]);

  return (
    <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-primary/20 shrink-0">
      <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center gap-2">

        {/* Mobile: back button when not on home; Desktop: always brand */}
        {!isHome && (
          <AppNavLink
            to={paths.root}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors shrink-0 md:hidden"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">{t("nav.myWeddings")}</span>
          </AppNavLink>
        )}

        {/* Brand — always visible on desktop, only on home on mobile */}
        <AppNavLink
          to={paths.root}
          className={cn(
            "flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity",
            !isHome && "hidden md:flex",
          )}
        >
          <Heart className="w-5 h-5 text-primary shrink-0" />
          <span className="font-serif italic text-xl text-primary truncate">
            Vite
          </span>
        </AppNavLink>

        {/* Spacer on mobile when back button is shown (brand is hidden) */}
        {!isHome && <span className="flex-1 md:hidden" />}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          role="switch"
          aria-checked={isDark}
          aria-label={themeLabel}
          title={themeLabel}
          className={cn(
            "relative h-6 w-11 rounded-full border-2 transition-all duration-300 cursor-pointer shrink-0",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            "hover:opacity-80 hover:scale-105 motion-reduce:hover:scale-100",
            isDark ? "bg-primary/30 border-primary/50" : "bg-amber-200 border-amber-300",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center",
              isDark ? "translate-x-[18px] bg-primary" : "translate-x-0.5 bg-amber-400",
            )}
          >
            {isDark
              ? <Moon className="h-2.5 w-2.5 text-white" />
              : <Sun className="h-2.5 w-2.5 text-white" />}
          </span>
        </button>

        <UserMenu refreshUnread={refreshUnread} />
      </div>
    </header>
  );
}
